/**
 * The armature check — "will World's animations play on this body?" — ONE
 * function, skeleton facts in, findings out (ADR-040; ADR-224 §4.1's codes
 * plus `no-skeleton`). World runs the same test on upload; Harry runs it on the
 * bytes World handed over and again on the bytes it exports, so "fixed in
 * Harry" means "passes World's gate" (ADR-224 §4.4). This is the seam that
 * later moves to the shared `@poqpoq/rig-check` package; nothing else in Harry
 * decides a verdict.
 *
 * WHAT IT MEASURES, in order:
 *   1. a skeleton at all (`no-skeleton`) and a real VRM file (`vrm-declared`,
 *      ADR-013 §7: Harry never repackages one);
 *   2. the family's body signature ⊆ the skin's names (World's `detectFamily`):
 *      `family-match`, `family-near-miss` (only the armature root is missing —
 *      ActorCore's `BoneRoot`), or `family-none` — which is a fix when the
 *      family canon can take the body (`planFamilyRename`) and an honest stop,
 *      reasons listed, when it cannot;
 *   3. the bind frames on every route that binds clips by name, through the
 *      BBSA mapping: the shared axis + world-space ROLL check (#194,
 *      `checkBindFrames`) against the family anchor — or the VRoid donor for a
 *      `J_Bip_*` body. A check that cannot measure says so
 *      (`frames-unmeasured`), never a silent pass;
 *   4. the limits that never block: `no-fingers`, `tail-rigid` / `tail-springs`.
 *
 * VERDICT: `unsupported` if any finding is a stop; else `needs-harry` if any
 * finding needs Harry; else `limited` if any limit; else `works`.
 *
 * The messages are World's words (ADR-224 §4.1 "the person reads"). Harry's
 * guided mode maps each code onto its own card (app/src/intake/intake-view.ts).
 *
 * Depends on: ir (RigDescriptor, Ontology), mapping (detect-family,
 * bind-frame-check), retarget/convention (hubToWorld), session/family-canon,
 * intake/bind-frame-anchors. Depended on by: the app's intake mode, tests.
 */
import type { Ontology } from '../ir/ontology.js';
import type { RigDescriptor } from '../ir/rig-descriptor.js';
import { detectRigFamily } from '../mapping/detect-family.js';
import { checkBindFrames, type AnatomicalFrame, type BindFrameReport, type BoneRest } from '../mapping/bind-frame-check.js';
import { hubToWorld } from '../retarget/convention.js';
import { canonSignature, planFamilyRename, type FamilyCanon, TRIPO_2026_HANDED_CANON } from '../session/family-canon.js';
import { BIND_FRAME_ANCHORS, type BindFrameAnchor } from './bind-frame-anchors.js';

export const RIG_CHECK_CODES = [
  'no-skeleton',
  'vrm-declared',
  'family-match',
  'family-near-miss',
  'family-none',
  'bind-frames',
  'frames-unmeasured',
  'no-fingers',
  'tail-rigid',
  'tail-springs',
  'tail-unrigged',
] as const;
export type RigCheckCode = (typeof RIG_CHECK_CODES)[number];

/** `unsupported` = nothing Harry can do today; never softened into `needs-harry`. */
export type RigCheckSeverity = 'info' | 'limit' | 'needs-harry' | 'unsupported';
export type RigCheckVerdict = 'works' | 'limited' | 'needs-harry' | 'unsupported';

export interface RigCheckFinding {
  readonly code: RigCheckCode;
  readonly severity: RigCheckSeverity;
  /** World's words — what the person reads (ADR-224 §4.1). */
  readonly message: string;
  /** The measured numbers behind it, for the status line (never the card). */
  readonly evidence: string;
}

export interface RigCheckReport {
  readonly verdict: RigCheckVerdict;
  /** Which library the body plays from when it works: the family id, `vrm`, or null. */
  readonly route: string | null;
  readonly findings: readonly RigCheckFinding[];
  /** The frame readings behind `bind-frames`, when measured (for the "before" number). */
  readonly frames: BindFrameReport | null;
}

/** File-level facts the skeleton cannot carry (the GLB's declared extensions). */
export interface RigCheckFileFacts {
  /** `VRMC_vrm` (or VRM 0.x `VRM`) declared — a real VRM, licence `meta` and all. */
  readonly vrmDeclared: boolean;
  /** `VRMC_springBone` declared. */
  readonly springBones: boolean;
}

export interface RigCheckInput {
  /** The body's skeleton as Harry's import door reads it; null = the file has none. */
  readonly rig: RigDescriptor | null;
  readonly ontology: Ontology;
  readonly file: RigCheckFileFacts;
  /** The family World plays (one today). Injected so a second family is data. */
  readonly canon?: FamilyCanon;
}

/** The bones the frame check reads (BBSA ids): spine chain, neck, upper arms, forearms, thighs, calves. */
export const RIG_CHECK_FRAME_BONES = ['Spine', 'Spine1', 'Spine2', 'Neck', 'LeftArm', 'LeftForeArm', 'RightArm', 'RightForeArm', 'LeftUpLeg', 'LeftLeg', 'RightUpLeg', 'RightLeg'] as const;

/** Signature bones whose absence is only the armature root's spelling (ActorCore `BoneRoot`). */
const ROOT_ONLY = new Set(['Root']);
const DIGIT_ID = /^(Left|Right)Hand(Thumb|Index|Middle|Ring|Pinky)\d$/;
const TAIL_NAME = /tail/i;

const stripGraft = (n: string): string => n.replace(/_graft$/, '');

/**
 * The frame check's inputs from a mapped rig: world rest per checked bone, its
 * BBSA primary child for the axis, and the hub triad's forward/up — the
 * derivation #194's corpus test pins (bind-frame-check.corpus.test.ts `inputsOf`).
 */
export function bindFrameInputs(rig: RigDescriptor, ontology: Ontology): { frame: AnatomicalFrame; bones: Record<string, BoneRest> } | null {
  const hub = hubToWorld(rig);
  if (hub === null) return null;
  const byName = new Map(rig.bones.map((b) => [b.name, b]));
  const byId = new Map<string, RigDescriptor['bones'][number]>();
  for (const m of rig.mapping) {
    const b = byName.get(m.rigBone);
    if (b !== undefined) byId.set(m.ontologyId, b);
  }
  const bones: Record<string, BoneRest> = {};
  for (const id of RIG_CHECK_FRAME_BONES) {
    const b = byId.get(id);
    if (b === undefined) continue;
    const childId = ontology.byId.get(id)?.primaryChild ?? null;
    const c = childId === null ? undefined : byId.get(childId);
    bones[id] = { rotation: b.worldBind.rotation, position: b.worldBind.position, childPosition: c?.worldBind.position ?? null };
  }
  return { frame: { forward: hub({ x: 0, y: 0, z: 1 }), up: hub({ x: 0, y: 1, z: 0 }) }, bones };
}

/** The worst-off bone of a frame report, as evidence ("RightArm roll −98°"). */
function worstBone(report: BindFrameReport): string {
  let worst: { id: string; what: string; deg: number } | null = null;
  for (const [id, r] of Object.entries(report.bones)) {
    const deg = r.verdict === 'axis' ? r.axisDeg : r.verdict === 'roll' && r.rollDeg !== null ? Math.abs(r.rollDeg) : -1;
    if (deg < 0) continue;
    if (worst === null || deg > worst.deg) worst = { id, what: r.verdict === 'axis' ? `axis ${r.axisDeg.toFixed(0)}°` : `roll ${r.rollDeg!.toFixed(0)}°`, deg };
  }
  const off = Object.values(report.bones).filter((r) => r.verdict === 'axis' || r.verdict === 'roll').length;
  return worst === null ? '' : `${off} of ${Object.keys(report.bones).length} bones off; worst ${worst.id} ${worst.what}`;
}

function frameFinding(rig: RigDescriptor, ontology: Ontology, anchor: BindFrameAnchor, against: string): { finding: RigCheckFinding | null; report: BindFrameReport | null } {
  const inputs = bindFrameInputs(rig, ontology);
  if (inputs === null) {
    return {
      finding: { code: 'frames-unmeasured', severity: 'info', message: "We couldn't check how the arms and legs are oriented.", evidence: 'the rig declares no usable up/facing convention' },
      report: null,
    };
  }
  const report = checkBindFrames(inputs.bones, inputs.frame, anchor.reference);
  if (report.verdict === 'bind-frames') {
    return { finding: { code: 'bind-frames', severity: 'needs-harry', message: 'Arms and legs would twist during animations.', evidence: `against ${against}: ${worstBone(report)}` }, report };
  }
  if (report.verdict === 'unmeasured') {
    return {
      finding: {
        code: 'frames-unmeasured',
        severity: 'info',
        message: "We couldn't check how the arms and legs are oriented.",
        evidence: report.missing.length > 0 ? `no measurable ${report.missing.join(', ')}` : 'no bone could be measured',
      },
      report,
    };
  }
  return { finding: null, report };
}

function verdictOf(findings: readonly RigCheckFinding[]): RigCheckVerdict {
  if (findings.some((f) => f.severity === 'unsupported')) return 'unsupported';
  if (findings.some((f) => f.severity === 'needs-harry')) return 'needs-harry';
  if (findings.some((f) => f.severity === 'limit')) return 'limited';
  return 'works';
}

/** The check. Never throws on a strange body: an unmeasurable fact is a finding. */
export function rigCheck(input: RigCheckInput): RigCheckReport {
  const canon = input.canon ?? TRIPO_2026_HANDED_CANON;
  const findings: RigCheckFinding[] = [];
  const done = (route: string | null, frames: BindFrameReport | null): RigCheckReport => ({ verdict: verdictOf(findings), route, findings, frames });

  if (input.file.vrmDeclared) {
    findings.push({
      code: 'vrm-declared',
      severity: 'unsupported',
      message: "Harry can't repackage a VRM without losing its license and expressions.",
      evidence: 'the file declares VRMC_vrm / VRM',
    });
  }
  const rig = input.rig;
  if (rig === null) {
    findings.push({
      code: 'no-skeleton',
      severity: 'unsupported',
      // World's reject text, verbatim (poqpoq-world #2219); Harry's card says "Harry can add a skeleton — coming soon."
      message: 'This model has no skeleton. Harry will soon be able to add one.',
      evidence: 'no skinned skeleton and no bones in the file',
    });
    return done(null, null);
  }

  const names = new Set(rig.bones.map((b) => stripGraft(b.name)));
  const missing = canonSignature(canon).filter((n) => !names.has(n));
  const isVroid = detectRigFamily(rig) === 'vrm';
  let route: string | null = null;
  let frames: BindFrameReport | null = null;

  if (missing.length === 0) {
    findings.push({ code: 'family-match', severity: 'info', message: 'Animations: full library.', evidence: `every ${canon.id} signature bone present` });
    route = canon.id;
    const f = frameFinding(rig, input.ontology, BIND_FRAME_ANCHORS['tripo-2026-handed'], canon.id);
    frames = f.report;
    if (f.finding !== null) findings.push(f.finding);
  } else if (isVroid) {
    // World's legacy VRM path binds the VRoid library by J_Bip names: the frames are judged against its donor.
    const f = frameFinding(rig, input.ontology, BIND_FRAME_ANCHORS.vrm, "World's VRoid library");
    frames = f.report;
    if (f.finding === null) {
      findings.push({ code: 'family-match', severity: 'info', message: 'Animations: full library.', evidence: "J_Bip names on VRoid frames — World's VRoid library" });
      route = 'vrm';
    } else {
      findings.push(f.finding);
    }
  } else {
    const plan = planFamilyRename(rig.bones, new Map(rig.mapping.map((m) => [m.rigBone, m.ontologyId])), canon);
    if (missing.length <= 2 && missing.every((n) => ROOT_ONLY.has(n)) && plan.refusals.length === 0) {
      findings.push({
        code: 'family-near-miss',
        severity: 'needs-harry',
        message: 'Bones are named slightly differently — Harry can rename them.',
        evidence: `missing ${missing.join(', ')}; ${plan.rename.size} bone(s) to rename`,
      });
    } else if (plan.refusals.length === 0) {
      findings.push({
        code: 'family-none',
        severity: 'needs-harry',
        message: "This body won't animate here yet — Harry can re-rig it.",
        evidence: `missing ${missing.length} of ${canonSignature(canon).length} ${canon.id} bones; ${plan.rename.size} rename(s), ${plan.supplied.length} supplied`,
      });
    } else {
      findings.push({
        code: 'family-none',
        severity: 'unsupported',
        message: `This body can't take World's skeleton: ${plan.refusals.join('; ')}.`,
        evidence: `missing ${missing.length} of ${canonSignature(canon).length} ${canon.id} bones`,
      });
      return done(null, null);
    }
    const f = frameFinding(rig, input.ontology, BIND_FRAME_ANCHORS['tripo-2026-handed'], canon.id);
    frames = f.report;
    if (f.finding !== null) findings.push(f.finding);
  }

  const mappedIds = new Set(rig.mapping.map((m) => m.ontologyId));
  const handMapped = mappedIds.has('LeftHand') || mappedIds.has('RightHand');
  if (handMapped && ![...mappedIds].some((id) => DIGIT_ID.test(id))) {
    findings.push({ code: 'no-fingers', severity: 'limit', message: "No fingers — hand poses won't show. Harry can add hands.", evidence: 'no digit chain mapped on either hand' });
  }
  const tails = rig.bones.filter((b) => TAIL_NAME.test(b.name)).length;
  if (tails > 0) {
    findings.push(
      input.file.springBones
        ? { code: 'tail-springs', severity: 'info', message: 'Your tail sways on its own.', evidence: `${tails} tail bone(s), VRMC_springBone declared` }
        : { code: 'tail-rigid', severity: 'limit', message: "Your tail won't move on its own.", evidence: `${tails} tail bone(s), no VRMC_springBone` },
    );
  }
  return done(route, frames);
}

/**
 * `tail-unrigged` (Harry-only, guided intake follow-ups 2026-10-03): a tail the MESH has and the skeleton
 * does not — the zone detector's tail offer behind the hips, owned by the pelvis. A skeleton-only check
 * cannot see it (ADR-224 §4.4 "tail unrigged"), so the app measures it on the loaded subject and adds the
 * finding here. A limit, never a stop: World's mapping reads it as an info line, not a reject.
 */
export function tailUnriggedFinding(evidence: string): RigCheckFinding {
  return { code: 'tail-unrigged', severity: 'limit', message: "Your tail won't move: Harry can give it bones.", evidence };
}

/** A report with Harry-measured findings added and its verdict recomputed (a stop stays a stop). */
export function withFindings(report: RigCheckReport, extra: readonly RigCheckFinding[]): RigCheckReport {
  if (extra.length === 0) return report;
  const findings = [...report.findings, ...extra.filter((e) => !report.findings.some((f) => f.code === e.code))];
  return { ...report, findings, verdict: verdictOf(findings) };
}

/**
 * The file facts from a GLB's JSON chunk (extensionsUsed / extensions). A file
 * that is not a readable GLB answers null — the caller's import door refuses it
 * with its own reason.
 */
export function rigCheckFileFacts(glb: ArrayBuffer): RigCheckFileFacts | null {
  if (glb.byteLength < 20) return null;
  const view = new DataView(glb);
  if (view.getUint32(0, true) !== 0x46546c67) return null; // 'glTF'
  const length = view.getUint32(12, true);
  if (view.getUint32(16, true) !== 0x4e4f534a || 20 + length > glb.byteLength) return null; // 'JSON'
  let json: { extensionsUsed?: unknown; extensions?: unknown };
  try {
    json = JSON.parse(new TextDecoder().decode(new Uint8Array(glb, 20, length))) as typeof json;
  } catch {
    return null;
  }
  const used = new Set<string>(Array.isArray(json.extensionsUsed) ? json.extensionsUsed.filter((e): e is string => typeof e === 'string') : []);
  const declared = typeof json.extensions === 'object' && json.extensions !== null ? Object.keys(json.extensions) : [];
  for (const d of declared) used.add(d);
  return { vrmDeclared: used.has('VRMC_vrm') || used.has('VRM'), springBones: used.has('VRMC_springBone') };
}
